import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'reducers';
import { ERevIntelDashboard } from 'types/revIntel';
import { JSONSerializable } from 'types/utils';

import { trackSegmentEvents } from 'actions/segment';

import { REV_INTEL_QUICKVIEW_EVENTS } from 'constants/revIntel';

const decamelizeKeys = require('decamelize-keys');

export const RevIntelEventView = {
  DEALS: 'DEALS',
  COMPANIES: 'COMPANIES',
  RI_REPORTS: 'RI_REPORTS',
  FORECAST: 'FORECAST',
  CONTACTS: 'CONTACTS',
  EMAIL_SYNC_SETTINGS: 'EMAIL_SYNC_SETTINGS',
  AI_METHODOLOGY: 'AI_METHODOLOGY',
  AI_METHODOLOGY_MENTOR: 'AI_METHODOLOGY_MENTOR'
} as const;

export const RevIntelEventAction = {
  PROPERTY_EDITED: 'PROPERTY_EDITED',
  QUICKVIEW_PROPERTY_EDITED: 'QUICKVIEW_PROPERTY_EDITED',
  PLAYBOOK_SUMMARY_EDITED: 'PLAYBOOK_SUMMARY_EDITED',
  LISTED: 'LISTED',
  VIEWED: 'VIEWED',
  DASHBOARD_VIEWED: 'DASHBOARD_VIEWED',
  UPDATED: 'UPDATED',
  ENABLED: 'ENABLED',
  ENABLED_CLICKED: 'ENABLED_CLICKED',
  DISABLED: 'DISABLED',
  DISABLED_CLICKED: 'DISABLED_CLICKED',
  SAVED: 'SAVED',
  NAVIGATED: 'NAVIGATED',
  PIPELINE_ASSIGNED: 'PIPELINE_ASSIGNED',
  FIELD_MAPPED: 'FIELD_MAPPED',
  TEMPLATE_SELECTED: 'TEMPLATE_SELECTED',
  SCORECARD_SELECTED: 'SCORECARD_SELECTED',
  DEAL_SCORING_SAVED: 'DEAL_SCORING_SAVED',
  AI_CRM_FIELD_MATCHES_APPLIED: 'AI_CRM_FIELD_MATCHES_APPLIED',
  CRM_FIELD_MAPPED: 'CRM_FIELD_MAPPED',
  DEAL_SCORE_TAB_VIEWED: 'DEAL_SCORE_TAB_VIEWED',
  LIVE_MENTOR_ENABLED: 'LIVE_MENTOR_ENABLED',
  LIVE_MENTOR_DISABLED: 'LIVE_MENTOR_DISABLED',
  LIVE_MENTOR_FAQ_VIEWED: 'LIVE_MENTOR_FAQ_VIEWED',
  // Methodology Mentor tracking actions
  EXPANDED: 'EXPANDED',
  COLLAPSED: 'COLLAPSED',
  TOPIC_HOVERED: 'TOPIC_HOVERED',
  TOPIC_COVERED: 'TOPIC_COVERED',
  MEETING_MIDDLE_PHASE_DETECTED: 'MEETING_MIDDLE_PHASE_DETECTED',
  MEETING_ENDING_SOON_DETECTED: 'MEETING_ENDING_SOON_DETECTED'
} as const;

export const RevIntelQuickViewEventMap = {
  [ERevIntelDashboard.deal]: RevIntelEventView.DEALS,
  [ERevIntelDashboard.company]: RevIntelEventView.COMPANIES,
  [ERevIntelDashboard.contact]: RevIntelEventView.CONTACTS,
  [ERevIntelDashboard.lead]: RevIntelEventView.CONTACTS, // Use same event view as contacts for now
  [ERevIntelDashboard.forecastSubmission]: RevIntelEventView.FORECAST
} as const;

export const RevIntelEventViewMap = {
  ...RevIntelQuickViewEventMap,
  AI_METHODOLOGY: RevIntelEventView.AI_METHODOLOGY
} as const;

export type TRevIntelEventView = keyof typeof RevIntelEventView;

// Automatically adds crm provide name to the event
const useTrackRevIntelEvents = () => {
  const crm = useSelector((state: RootState) => state.user?.crmProvider);
  const dispatch = useDispatch();

  const trackRevIntelEvent = useCallback(
    (params: {
      view: keyof typeof RevIntelEventView;
      action: keyof typeof RevIntelEventAction;
      props: Record<string, JSONSerializable>;
    }) => {
      dispatch(
        trackSegmentEvents({
          type: `${params.view}_${params.action}`,
          crm_provider_name: crm,
          ...decamelizeKeys(params.props, { deep: true })
        })
      );
    },
    [crm, dispatch]
  );

  const trackRevIntelQuickviewEvent = useCallback(
    (params: {
      dashboardKey: ERevIntelDashboard;
      tab: keyof typeof REV_INTEL_QUICKVIEW_EVENTS;
      action: keyof typeof RevIntelEventAction;
      props: Record<string, JSONSerializable>;
    }) => {
      if (
        !RevIntelQuickViewEventMap[params.dashboardKey] ||
        !REV_INTEL_QUICKVIEW_EVENTS[params.tab] ||
        !RevIntelEventAction[params.action]
      ) {
        return;
      }

      dispatch(
        trackSegmentEvents({
          type: `${RevIntelQuickViewEventMap[
            params.dashboardKey as keyof typeof RevIntelQuickViewEventMap
          ]
            }_QUICKVIEW_${REV_INTEL_QUICKVIEW_EVENTS[params.tab]}_${params.action
            }`,
          crm_provider_name: crm,
          ...decamelizeKeys(params.props, { deep: true })
        })
      );
    },
    [crm, dispatch]
  );

  const trackAiMethodologyEvent = useCallback((params: {
    action: keyof typeof RevIntelEventAction,
    props: Record<string, JSONSerializable>,
    view?: keyof typeof RevIntelEventView
  }) => {
    const viewName = params.view || 'AI_METHODOLOGY';
    dispatch(
      trackSegmentEvents({
        type: `${RevIntelEventView[viewName]}_${params.action}`,
        crm_provider_name: crm,
        ...decamelizeKeys(params.props, { deep: true })
      })
    );
  }, [crm, dispatch]);

  return {
    trackRevIntelEvent,
    trackRevIntelQuickviewEvent,
    trackAiMethodologyEvent
  };
};

export default useTrackRevIntelEvents;
